import { t } from "@lingui/macro"
import { GridRenderCellParams } from "@mui/x-data-grid-pro"
import { useCallback, useMemo } from "react"

import { AccessTypeHeader } from "src/components/AccessTypeHeader"
import { SegmentIdsCell } from "src/components/TableCells/SegmentIdsCell"
import { Time } from "src/components/Time"
import { IColumn } from "src/types/data-grid-pro"
import { IPollsItem } from "src/views/polls/types/polls-item"
import { PollStatusChip } from "src/modals/polls/components/PollStatusChip"
import {
    POLL_STATUS,
    POLL_STATUS_OPTIONS,
} from "src/shared/polls/constants/polls-status"
import { RowCellContainer } from "src/components/RowCellContainer"

export const useGetPollColumns = (): IColumn<IPollsItem>[] => {
    const statusOptions = useMemo(
        () => [
            {
                value: POLL_STATUS.OPEN,
                label: POLL_STATUS_OPTIONS.open.name,
            },
            {
                value: POLL_STATUS.CLOSED,
                label: POLL_STATUS_OPTIONS.closed.name,
            },
            {
                value: POLL_STATUS.SCHEDULED,
                label: POLL_STATUS_OPTIONS.scheduled.name,
            },
            {
                value: POLL_STATUS.DRAFT,
                label: POLL_STATUS_OPTIONS.draft.name,
            },
            {
                value: POLL_STATUS.DELETED,
                label: POLL_STATUS_OPTIONS.deleted.name,
            },
        ],
        [],
    )

    const renderSegmentIdsCell = useCallback(
        (params: GridRenderCellParams) => (
            <SegmentIdsCell segmentNames={params.value} />
        ),
        [],
    )

    const renderAccessTypeCell = useCallback(
        (params: GridRenderCellParams) => (
            <AccessTypeHeader accessType={params.value} />
        ),
        [],
    )

    const renderDateCell = useCallback((params: GridRenderCellParams) => {
        return <Time date={params.value} />
    }, [])

    const renderStatusCell = useCallback((params: GridRenderCellParams) => {
        return <PollStatusChip status={params.value as POLL_STATUS} />
    }, [])

    const renderVotesCountCell = useCallback((params: GridRenderCellParams) => {
        return (
            <RowCellContainer>
                {params.value === 0 ? t`polls.no-votes` : params.value}
            </RowCellContainer>
        )
    }, [])

    return useMemo(
        () => [
            {
                field: "poll_id",
                headerName: t`polls.id`,
                filterable: false,
                sortable: false,
                width: 100,
            },
            {
                field: "status",
                headerName: t`polls.status`,
                filterable: true,
                sortable: true,
                width: 120,
                renderCell: renderStatusCell,
                type: "singleSelect",
                valueOptions: statusOptions,
            },
            {
                field: "question",
                headerName: t`polls.question`,
                flex: 2,
                type: "string",
            },
            {
                field: "close_at",
                headerName: t`polls.end-date`,
                filterable: false,
                flex: 1,
                renderCell: renderDateCell,
                type: "date",
            },
            {
                field: "votes_count",
                headerName: t`polls.votes`,
                filterable: false,
                sortable: true,
                renderCell: renderVotesCountCell,
                width: 100,
            },
            {
                field: "segment_names",
                headerName: t`polls.visible-for`,
                filterable: false,
                sortable: false,
                flex: 1,
                renderCell: renderSegmentIdsCell,
            },
            {
                field: "access_type",
                headerName: t`polls.access`,
                filterable: false,
                sortable: false,
                flex: 1,
                renderCell: renderAccessTypeCell,
            },
        ],
        [
            renderAccessTypeCell,
            renderSegmentIdsCell,
            renderDateCell,
            renderStatusCell,
            renderVotesCountCell,
            statusOptions,
        ],
    )
}
